/**
 * "Bu bilgisayar hangi cihaz?" — tarayıcı, aynı makinede çalışan Octo.Agent'ın döngü
 * adresine (127.0.0.1) sorar. Böylece tarayıcı ile ajanı kullanıcı elle eşleştirmez;
 * "yerel yazıcı" (@local) ve "yerel tartı" bu kimlikle çözülür.
 *
 * Ajan yoksa istek saniyenin altında reddedilir ve null döner; hiçbir şey kırılmaz.
 * Döngü adresi tarayıcıda "güvenilir" sayılır, https sayfadan da çağrılabilir.
 * React'e bağımlı değil: işlem çalıştırıcı (actionRunner) da doğrudan çağırır.
 */
export const LOCAL_AGENT_PORT = 47621;

export interface LocalDeviceInfo {
  deviceId: string;
  deviceCode: string;
  machineName: string;
  agentVersion: string | null;
  /** Ajan şu an DAL'a bağlı mı. */
  connected: boolean;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const CACHE_MS = 15_000;
let cached: { at: number; value: LocalDeviceInfo | null } | null = null;
let inflight: Promise<LocalDeviceInfo | null> | null = null;

/** Yerel ajan kimliği; kısa süre önbellekte tutulur, eşzamanlı çağrılar tek istekte birleşir. */
export function fetchLocalDevice(port = LOCAL_AGENT_PORT): Promise<LocalDeviceInfo | null> {
  if (cached && Date.now() - cached.at < CACHE_MS) return Promise.resolve(cached.value);
  if (inflight) return inflight;
  inflight = fetchLocalDeviceUncached(port)
    .then((value) => {
      cached = { at: Date.now(), value };
      return value;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export async function fetchLocalDeviceUncached(port = LOCAL_AGENT_PORT): Promise<LocalDeviceInfo | null> {
  try {
    const res = await fetch(`http://127.0.0.1:${port}/whoami`, {
      signal: AbortSignal.timeout(1500),
      cache: 'no-store',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<LocalDeviceInfo>;
    // Eşleşmemiş ajan boş kimlikle döner: "cihaz yok" sayılır.
    if (!body.deviceId || body.deviceId === EMPTY_GUID) return null;
    return {
      deviceId: body.deviceId,
      deviceCode: body.deviceCode ?? '',
      machineName: body.machineName ?? '',
      agentVersion: body.agentVersion ?? null,
      connected: !!body.connected,
    };
  } catch {
    return null;
  }
}
